import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { Cookie, ArrowLeft } from "lucide-react";

/**
 * /cookies — plain-English cookie policy.
 *
 * Sister page to /privacy and /security. Keeps the cookie-by-cookie list
 * out of the Privacy Policy so the policy reads more like guidance and
 * less like a regulatory disclosure form. Both pages cross-link.
 */
export default function Cookies() {
    return (
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16 leading-relaxed text-[#1F2624]">
            <SeoHead
                title="Memory Lane Care — Cookie Policy"
                description="A plain-English list of the cookies Memory Lane uses, what each one is for, and how to opt out. No ad-network tracking — only what's needed to keep you signed in, remember preferences, and understand how the product is used."
                canonicalPath="/cookies"
            />
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6">
                <ArrowLeft size={14} strokeWidth={1.8} /> Back to Memory Lane
            </Link>
            <header className="border-b border-[#E2E8F0] pb-6 mb-8">
                <div className="flex items-center gap-2.5 text-teal-dark mb-3">
                    <Cookie size={22} strokeWidth={1.6} />
                    <span className="text-sm uppercase tracking-wider font-semibold">Memory Lane Care Navigator</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight">Cookie Policy</h1>
                <p className="mt-3 text-sm text-[#4C5551]">Last updated: February 2026</p>
            </header>

            <Section title="In plain English">
                <p>
                    Memory Lane uses cookies and a small amount of similar browser storage to keep
                    you signed in, remember a few small preferences, and understand how the product
                    is being used in aggregate. We do <strong>not</strong> use ad-network cookies,
                    cross-site advertising pixels, or any tracker that would follow you around the
                    rest of the web.
                </p>
                <p>
                    If you want to read the full data-handling picture first, our{" "}
                    <Link to="/privacy" className="text-teal-dark underline">Privacy Policy</Link>{" "}
                    has the context.
                </p>
            </Section>

            <Section title="Strictly necessary">
                <p>
                    These are what makes the app actually work. Disabling them will sign you out and
                    make Memory Lane unable to remember anything between visits.
                </p>
                <CookieTable rows={[
                    { name: "ml_token (localStorage)", purpose: "Keeps you signed in across page loads.", lifespan: "Until you sign out or clear browser storage." },
                    { name: "mlcn_session_id (localStorage)", purpose: "Anchors your caregiving data (intake, profile, behaviors, Lane memory) to your account.", lifespan: "Until you sign out and explicitly clear browser storage." },
                ]} />
            </Section>

            <Section title="Preferences">
                <p>
                    These remember small choices you&apos;ve already made so we don&apos;t ask again.
                </p>
                <CookieTable rows={[
                    { name: "mlcn_country (localStorage)", purpose: "The country you picked at sign-in so we show locale-specific resources (GUIDE eligibility, NHS routes, etc.).", lifespan: "Until you change it." },
                    { name: "ml_lane_seed_prompt (localStorage)", purpose: "Briefly carries a search query from a public article into the Lane chat so you don\u2019t have to retype it.", lifespan: "Consumed on first use; deleted automatically." },
                    { name: "ml_ga_* (sessionStorage)", purpose: "Prevents analytics events from double-firing in the same browser session (e.g. one Roadmap Started per visit, not per click).", lifespan: "Until you close the tab." },
                ]} />
            </Section>

            <Section title="Analytics">
                <p>
                    Aggregated, pseudonymous tools that help us understand how Memory Lane is used —
                    which articles are working, where caregivers get stuck, which CTAs convert.
                    None of these are tied to advertising networks.
                </p>
                <CookieTable rows={[
                    { name: "_ga, _ga_*", purpose: "Google Analytics 4. Anonymized, with IP truncation. Tells us page views, time on page, and conversion events (Account Created, Roadmap Completed, etc.). We do not send your name or email to Google.", lifespan: "Up to 13 months." },
                    { name: "ph_*", purpose: "PostHog product analytics. Identifies a pseudonymous visitor so we can see drop-off in flows (e.g. step 3 of intake). When session replay is enabled, sensitive form fields are masked.", lifespan: "Up to 12 months." },
                    { name: "_clck, _clsk", purpose: "Microsoft Clarity heatmaps and aggregated session insights. Sensitive form fields are masked. Clarity is a first-party tool that does not feed advertising networks. Only set after you opt in via the cookie banner.", lifespan: "Up to 12 months." },
                ]} />
                <p className="text-sm text-[#4C5551] mt-2">
                    You can opt out of these at the browser level using any standard tracker blocker
                    (uBlock Origin, Privacy Badger, your browser&apos;s built-in tracking protection,
                    Global Privacy Control). Memory Lane will continue to work normally.
                </p>
            </Section>

            <Section title="Opt out anytime">
                <p>
                    On your first visit you&apos;ll see a small consent banner. <strong>Accept</strong>{" "}
                    turns on Google Analytics 4 and Microsoft Clarity. <strong>Decline</strong>{" "}
                    leaves both off — GA4 stays in Consent Mode &quot;denied&quot; (no analytics cookies
                    set) and Clarity isn&apos;t loaded at all.
                </p>
                <p>
                    If your browser sends a Global Privacy Control signal, we treat that as a
                    decision to deny and don&apos;t show the banner. You can also clear this site&apos;s
                    storage in your browser settings to re-open the banner.
                </p>
            </Section>

            <Section title="Third-party widgets">
                <p>
                    Some pages embed widgets from other services. When they load, those services
                    may set their own cookies. We keep this list short on purpose:
                </p>
                <CookieTable rows={[
                    { name: "Calendly", purpose: "Loads on /book and on the inline scheduling widgets. Calendly sets cookies to remember your time-zone and booking state.", lifespan: "Per Calendly's own policy." },
                    { name: "Stripe Checkout", purpose: "Loads only when you start a Care Navigator booking. Stripe sets fraud-prevention cookies during checkout.", lifespan: "Per Stripe's own policy." },
                ]} />
            </Section>

            <Section title="What we don\u2019t use">
                <ul>
                    <li>No ad-network cookies (no Facebook Pixel, no Google Ads remarketing, no TikTok pixel, no LinkedIn Insight Tag).</li>
                    <li>No cross-site identifiers sold or shared with data brokers.</li>
                    <li>No fingerprinting beyond what GA4/PostHog/Clarity do as part of their stock SDKs.</li>
                </ul>
            </Section>

            <Section title="Changes">
                <p>
                    If we add a meaningful new cookie or analytics tool, we&apos;ll update this page and
                    note it in the next release of the Privacy Policy.
                </p>
            </Section>

            <Section title="Contact">
                <p>
                    Questions about cookies, opt-outs, or data deletion:{" "}
                    <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>.
                </p>
            </Section>
        </article>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold tracking-tight mb-3">{title}</h2>
            <div className="space-y-3 text-base">{children}</div>
        </section>
    );
}

function CookieTable({ rows }) {
    return (
        <div className="overflow-x-auto rounded-2xl border border-cream-dark mt-2">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-cream-light text-left text-xs uppercase tracking-wider text-[#4C5551] border-b border-cream-dark">
                        <th className="py-2 px-3 font-semibold">Cookie / storage</th>
                        <th className="py-2 px-3 font-semibold">What it&apos;s for</th>
                        <th className="py-2 px-3 font-semibold">How long it lasts</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-cream-light last:border-b-0 align-top">
                            <td className="py-2 px-3 font-medium text-[#1F2624] whitespace-nowrap">{r.name}</td>
                            <td className="py-2 px-3 text-[#4C5551]">{r.purpose}</td>
                            <td className="py-2 px-3 text-[#4C5551]">{r.lifespan}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
